// The one place that knows where the backend lives. Both apps import this;
// each then builds its own client on top, so the signage bundle never carries
// admin endpoints and vice versa.
const API_BASE = (import.meta.env.VITE_API_BASE || 'http://localhost:8080').replace(/\/$/, '');

// Retrying happens here rather than in every caller, because the alternative
// is what this had: no retry anywhere, and one loop that retried with no delay
// at all. Three rules, and the reasons matter more than the numbers.
//
// Only a GET is retried. Every POST in this service creates something - a
// pairing, a session, a claim, a queued sync - and replaying one costs an
// orphaned row or a second credential. A GET can be repeated safely, so a GET
// is what repeats.
//
// Only a transient answer is retried: a dropped connection, a timeout, a 429,
// a 5xx. A 4xx is the server saying the request itself is wrong, and sending
// it again unchanged cannot make it right.
//
// The wait is a random point inside the window rather than the whole of it -
// full jitter. Displays fail together, because they lose power together and
// come back together, and a fleet retrying in lockstep is what keeps a backend
// down once it is down. Spreading the retries is most of the protection here;
// the backoff alone is not.
const MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 500;
const MAX_DELAY_MS = 8000;
const RETRY_AFTER_CAP_MS = 30000;

// A request that never answers is worse than one that fails, because nothing
// above notices - a wedged connection holds its slot for as long as the
// browser feels like it, and on a display there is nobody to press reload.
const TIMEOUT_MS = 15000;

const RETRYABLE = new Set([408, 425, 429, 500, 502, 503, 504]);

// The API stamps every response with the build that served it. Watching it
// costs no request of its own - it rides on the ones the caller was making
// anyway - which is why it is a header rather than a /version endpoint.
const VERSION_HEADER = 'X-Litbyt-Version';
let versionListener = null;

// Only the signage app listens. The admin site must never reload itself out
// from under somebody who is halfway through typing into a form.
export function onServerVersion(listener) {
  versionListener = listener;
}

const backoffMs = (attempt) => Math.random() * Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempt);

// A server that says how long to wait knows better than the formula does.
function retryAfterMs(response) {
  const header = response.headers.get('Retry-After');
  if (!header) return null;

  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.min(Math.max(seconds, 0) * 1000, RETRY_AFTER_CAP_MS);

  const at = Date.parse(header);
  if (Number.isNaN(at)) return null;
  return Math.min(Math.max(at - Date.now(), 0), RETRY_AFTER_CAP_MS);
}

function wait(ms, signal) {
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, ms);

    if (!signal) return;
    if (signal.aborted) abort();
    else signal.addEventListener('abort', abort, { once: true });
  });
}

// One attempt, under both the caller's cancellation and our own timeout.
async function send(path, options, headers, credentials, callerSignal) {
  const controller = new AbortController();
  const forward = () => controller.abort(callerSignal.reason);
  const timer = setTimeout(() => controller.abort(new DOMException('Timed out', 'TimeoutError')), TIMEOUT_MS);

  if (callerSignal) {
    if (callerSignal.aborted) forward();
    else callerSignal.addEventListener('abort', forward, { once: true });
  }

  try {
    return await fetch(`${API_BASE}${path}`, { ...options, headers, credentials, signal: controller.signal });
  } finally {
    clearTimeout(timer);
    callerSignal?.removeEventListener('abort', forward);
  }
}

// options.as = 'blob' answers with the response body as a Blob rather than
// parsed JSON, for a route that sends a file.
//
// credentials is per app rather than fixed here. The admin site needs its
// session cookie sent; the signage app must NOT send cookies, because it
// authenticates with a bearer token and a stray cookie would authenticate it
// as whoever last signed in - cookies ignore port, so on localhost the admin
// session reaches the signage origin.
export async function request(path, options = {}, extraHeaders = {}, credentials = 'omit') {
  const method = (options.method || 'GET').toUpperCase();
  const repeatable = method === 'GET';
  const signal = options.signal;
  const headers = { 'Content-Type': 'application/json', ...extraHeaders, ...(options.headers || {}) };

  for (let attempt = 0; ; attempt++) {
    const lastTry = !repeatable || attempt >= MAX_ATTEMPTS - 1;
    let response;

    try {
      response = await send(path, options, headers, credentials, signal);
    } catch (error) {
      // A caller that cancelled is not a failure to recover from - it is the
      // caller saying it no longer wants the answer.
      if (signal?.aborted || lastTry) throw error;
      await wait(backoffMs(attempt), signal);
      continue;
    }

    if (!lastTry && RETRYABLE.has(response.status)) {
      await wait(retryAfterMs(response) ?? backoffMs(attempt), signal);
      continue;
    }

    if (versionListener) {
      const version = response.headers.get(VERSION_HEADER);
      // Absent on a response API Gateway generated itself - a throttle, a
      // timeout - which is not the API announcing a new build.
      if (version) versionListener(version);
    }

    // A download. Only a success is a file: a refusal is the same JSON error
    // as anywhere else, and is read as one below.
    if (response.ok && options.as === 'blob') {
      return response.blob();
    }

    const body = await response.json().catch(() => ({}));
    // A pairing poll answers 202 while the claim is still outstanding. That is a
    // state, not a failure, so it must not throw.
    if (response.status === 202) {
      return { ...body, pending: true };
    }
    if (!response.ok) {
      const error = new Error(body.detail || body.error || `${method} ${path} failed: ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return body;
  }
}
